import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreferences, useUpdatePreferences } from './usePreferences';
import { applyMilestoneOrder } from '../components/kanban/milestoneOrder';
import type { UserPreferences } from '../services/api/preferences';

/**
 * Quick 260914-huu — per-user, per-project drag-to-reorder of the Kanban
 * milestone swimlane rows. Sibling of `useFoldState` / `useColumnCollapse`
 * (same debounced-full-object-PATCH pattern), but the stored value is an
 * ordering OVERLAY rather than a boolean map — see `milestoneOrder.ts` for
 * the ordering contract.
 *
 * Reads from the existing `usePreferences` TanStack Query cache (no extra
 * fetch). `orderMilestones` is a pure projection of that cache entry through
 * `applyMilestoneOrder` — it never mutates or writes anything.
 *
 * Write path (`onReorder`) differs from `useFoldState`/`useColumnCollapse` in
 * two ways, both intentional:
 *
 * 1. No pending accumulator. `useFoldState`'s CR-03 fix exists because a fold
 *    toggle is an incremental DELTA (add/remove one key) and re-reading the
 *    stale cached value inside the debounce window would drop an earlier
 *    toggle in the same window. Here every drop produces a complete
 *    ABSOLUTE ordering of every milestone visible in this project's row list
 *    — the latest write already subsumes any earlier write in the window, so
 *    there is nothing to accumulate.
 *
 * 2. The optimistic cache apply happens IMMEDIATELY on drop, not deferred
 *    into the debounce timer. A row drag is a direct-manipulation gesture:
 *    a drop that visibly does nothing for 250ms and then jumps into place
 *    reads as broken, whereas the equivalent delay before a fold/unfold
 *    animates (F-1107) is an accepted trade-off for a lower-intent click.
 *    The debounce still exists to coalesce rapid repeat drags into one
 *    PATCH; it just no longer gates the visible reorder.
 *
 * Rollback: a snapshot of the whole `milestoneOrder` map is captured at the
 * FIRST optimistic apply of a debounce window (not on every call — a second
 * drop before the timer fires must not overwrite the snapshot with the
 * already-optimistic value, or a mid-window failure would roll back to the
 * wrong state). `onError` restores that snapshot via a per-call callback,
 * which in TanStack Query v5 runs AFTER `useUpdatePreferences`'s own
 * mutation-level `onError` — so ours wins, replacing its (already stale)
 * rollback with the correct pre-drag one. `onSettled` clears the snapshot so
 * the next debounce window starts clean.
 */
export function useMilestoneOrder(projectId: string): {
  orderMilestones: <T extends { id: number }>(milestones: T[]) => T[];
  onReorder: (nextIds: number[]) => void;
} {
  const { data: preferences } = usePreferences();
  const updateMutation = useUpdatePreferences();
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pre-drag snapshot of the FULL milestoneOrder map, captured once per
  // debounce window (see docblock above for why this is not a per-call
  // accumulator like useFoldState's pendingRef).
  const snapshotRef = useRef<Record<string, number[]> | undefined | null>(null);

  const storedOrder = useMemo<number[] | undefined>(
    () => preferences?.milestoneOrder?.[projectId],
    [preferences, projectId],
  );

  const orderMilestones = useCallback(
    <T extends { id: number }>(milestones: T[]) => applyMilestoneOrder(milestones, storedOrder),
    [storedOrder],
  );

  const onReorder = useCallback(
    (nextIds: number[]) => {
      // Capture the pre-drag snapshot only on the first call of a window.
      if (snapshotRef.current === null) {
        snapshotRef.current = preferences?.milestoneOrder;
      }
      const snapshot = snapshotRef.current;

      const nextMap: Record<string, number[]> = {
        ...preferences?.milestoneOrder, // PRESERVE other projects' entries (Pitfall 3)
        [projectId]: nextIds,
      };

      // Immediate optimistic cache apply — see docblock point 2.
      queryClient.setQueryData<{ data: UserPreferences }>(['preferences'], (old) => ({
        data: { ...(old?.data ?? {}), milestoneOrder: nextMap },
      }));

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        updateMutation.mutate(
          { milestoneOrder: nextMap },
          {
            onError: () => {
              queryClient.setQueryData<{ data: UserPreferences }>(['preferences'], (old) => ({
                data: { ...(old?.data ?? {}), milestoneOrder: snapshot },
              }));
            },
            onSettled: () => {
              snapshotRef.current = null; // window closed — next drop starts a fresh snapshot
            },
          },
        );
      }, 250);
    },
    [preferences, projectId, queryClient, updateMutation],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return { orderMilestones, onReorder };
}
